import { Padding, SVG_NS, attachHoverListeners, observeResize } from './shared';

/**
 * Configuration for a bar chart instance.
 */
export interface BarChartConfig {
  /** Array of data series. Each inner array is one series of numeric values. */
  data: number[][];
  /** Category labels. Must match the length of each data series. */
  labels: string[];
  /** X-axis title. */
  xLabel?: string;
  /** Y-axis title. */
  yLabel?: string;
  /** Colors for each series. Defaults to Thruscan brand palette. */
  colors?: string[];
  /** Chart width in pixels. Defaults to container's clientWidth or 600. */
  width?: number;
  /** Chart height in pixels. Defaults to 350. */
  height?: number;
  /** Stack series instead of grouping side-by-side. Default false. */
  stacked?: boolean;
  /** Horizontal bars (categories on Y-axis). Default false. */
  horizontal?: boolean;
  /** Gap between bar groups as fraction of group width (0–1). Default 0.2. */
  barGap?: number;
  /** Show tick labels. Default true. */
  showTicks?: boolean;
  /** Show grid lines. Default true. */
  showGrid?: boolean;
  /** Dashed grid lines. Default false. */
  gridDashed?: boolean;
  /** Enable hover tooltip + indicator. Default true. */
  hover?: boolean;
  /** Chart padding {top, right, bottom, left}. Default {top:20, right:30, bottom:50, left:60} (left:80 for horizontal). */
  padding?: Partial<Padding>;
}


/**
 * Creates a high-performance SVG+HTML hybrid bar chart inside the specified container.
 */
export function NanoBarChart(containerId: string, config: BarChartConfig): void {
  var data = config.data;
  var labels = config.labels;
  var xLabel = config.xLabel || '';
  var yLabel = config.yLabel || '';
  var colors = config.colors || ['#215CAF', '#007894', '#00C9A7', '#DE4437', '#F69C3D'];
  var stacked = config.stacked === true;
  var horizontal = config.horizontal === true;
  var showTicks = config.showTicks !== false;
  var showGrid = config.showGrid !== false;
  var gridDashed = config.gridDashed === true;
  var hoverEnabled = config.hover !== false;
  var barGap = config.barGap !== undefined ? config.barGap : 0.2;
  var dp = config.padding || {};
  var padding: Padding = { top: dp.top || 0, right: dp.right || 0, bottom: dp.bottom || 0, left: dp.left || 0 };

  var container = document.getElementById(containerId)!;
  if (!container || !data.length || !data[0].length) return;
  var chartWidth = config.width || container.clientWidth || 600;
  var chartHeight = config.height || 350;
  var plotWidth = chartWidth - padding.left - padding.right;
  var plotHeight = chartHeight - padding.top - padding.bottom;
  var plotBottom = padding.top + plotHeight;

  var seriesCount = data.length;
  var catCount = labels.length;
  var si: number, ci: number, val: number, ti: number;

  // ── Phase 1: Pure math (zero DOM) ──

  // Find min/max
  var minVal: number, maxVal: number;
  if (stacked) {
    minVal = 0;
    maxVal = 0;
    for (ci = 0; ci < catCount; ci++) {
      var colSum = 0;
      for (si = 0; si < seriesCount; si++) {
        colSum += data[si][ci];
        if (data[si][ci] < minVal) minVal = data[si][ci];
      }
      if (colSum > maxVal) maxVal = colSum;
    }
  } else {
    minVal = maxVal = data[0][0];
    for (si = 0; si < seriesCount; si++)
      for (ci = 0; ci < catCount; ci++) {
        val = data[si][ci];
        if (val < minVal) minVal = val;
        if (val > maxVal) maxVal = val;
      }
  }

  if (minVal > 0) minVal = 0;
  if (maxVal < 0) maxVal = 0;

  var range = maxVal - minVal || 1;
  maxVal += range * 0.05;
  range = maxVal - minVal;

  // Grid tick positions + labels
  var tickCount = 6;
  var tickStep = range / (tickCount - 1);
  var gridPositions = new Int16Array(tickCount);
  var gridLabels = new Array<string>(tickCount);

  var totalBars = seriesCount * catCount;
  var barX = new Int16Array(totalBars);
  var barY = new Int16Array(totalBars);
  var barW = new Int16Array(totalBars);
  var barH = new Int16Array(totalBars);
  var groupSize: number;
  var zeroY = 0;

  if (horizontal) {
    // ── Horizontal: categories on Y, values on X ──
    groupSize = plotHeight / catCount;
    var zeroX = (padding.left + ((0 - minVal) / range) * plotWidth) | 0;

    for (ti = 0; ti < tickCount; ti++) {
      var tickVal = minVal + ti * tickStep;
      gridPositions[ti] = (padding.left + ((tickVal - minVal) / range) * plotWidth) | 0;
      gridLabels[ti] = tickVal % 1 === 0 ? tickVal.toString() : tickVal.toFixed(1);
    }

    var hBarHeight = ((groupSize * (1 - barGap)) / seriesCount) | 0;
    var hGroupStart = ((groupSize - hBarHeight * seriesCount) / 2) | 0;

    for (si = 0; si < seriesCount; si++)
      for (ci = 0; ci < catCount; ci++) {
        var idx = si * catCount + ci;
        val = data[si][ci];
        var valX = (padding.left + ((val - minVal) / range) * plotWidth) | 0;

        barY[idx] = (padding.top + ci * groupSize + hGroupStart + si * hBarHeight) | 0;
        barH[idx] = hBarHeight;

        if (val >= 0) {
          barX[idx] = zeroX;
          barW[idx] = (valX - zeroX || 1) as number;
        } else {
          barX[idx] = valX;
          barW[idx] = (zeroX - valX || 1) as number;
        }
      }
  } else {
    // ── Vertical: categories on X, values on Y ──
    groupSize = plotWidth / catCount;

    zeroY = (padding.top + plotHeight - ((0 - minVal) / range) * plotHeight) | 0;

    for (ti = 0; ti < tickCount; ti++) {
      var tickVal = minVal + ti * tickStep;
      gridPositions[ti] = (padding.top + plotHeight - ((tickVal - minVal) / range) * plotHeight) | 0;
      gridLabels[ti] = tickVal % 1 === 0 ? tickVal.toString() : tickVal.toFixed(1);
    }

    if (stacked) {
      var stackBarWidth = (groupSize * (1 - barGap)) | 0;
      var stackBarOffset = ((groupSize - stackBarWidth) / 2) | 0;

      for (ci = 0; ci < catCount; ci++) {
        var cumPos = 0;
        for (si = 0; si < seriesCount; si++) {
          var idx = si * catCount + ci;
          val = data[si][ci];
          var baseY = (padding.top + plotHeight - ((cumPos - minVal) / range) * plotHeight) | 0;
          var topY = (padding.top + plotHeight - ((cumPos + val - minVal) / range) * plotHeight) | 0;
          cumPos += val;

          barX[idx] = (padding.left + ci * groupSize + stackBarOffset) | 0;
          barY[idx] = Math.min(baseY, topY);
          barW[idx] = stackBarWidth;
          barH[idx] = (Math.abs(topY - baseY) || 1) as number;
        }
      }
    } else {
      var singleBarWidth = ((groupSize * (1 - barGap)) / seriesCount) | 0;
      var groupBarStart = ((groupSize - singleBarWidth * seriesCount) / 2) | 0;

      for (si = 0; si < seriesCount; si++)
        for (ci = 0; ci < catCount; ci++) {
          var idx = si * catCount + ci;
          val = data[si][ci];
          var topY = (padding.top + plotHeight - ((val - minVal) / range) * plotHeight) | 0;

          barX[idx] = (padding.left + ci * groupSize + groupBarStart + si * singleBarWidth) | 0;
          barW[idx] = singleBarWidth;

          if (val >= 0) {
            barY[idx] = topY;
            barH[idx] = (zeroY - topY || 1) as number;
          } else {
            barY[idx] = zeroY;
            barH[idx] = (topY - zeroY || 1) as number;
          }
        }
    }
  }

  // ── Phase 2: Build SVG string ──
  var svgMarkup = '<svg xmlns="http://www.w3.org/2000/svg" width="' + chartWidth + '" height="' + chartHeight + '" viewBox="0 0 ' + chartWidth + ' ' + chartHeight + '" class="chart">';

  // Grid
  if (showGrid) {
    var gridPath = '';
    if (horizontal) {
      for (ti = 0; ti < tickCount; ti++)
        gridPath += 'M' + gridPositions[ti] + ',' + padding.top + 'V' + plotBottom;
    } else {
      for (ti = 0; ti < tickCount; ti++)
        gridPath += 'M' + padding.left + ',' + gridPositions[ti] + 'H' + (padding.left + plotWidth);
    }
    svgMarkup += '<path d="' + gridPath + '" class="chart-grid"' + (gridDashed ? ' stroke-dasharray="3 4"' : '') + '/>';
  }

  // Zero line (if bi-polar)
  if (minVal < 0) {
    if (horizontal) {
      var zx = (padding.left + ((0 - minVal) / range) * plotWidth) | 0;
      svgMarkup += '<line x1="' + zx + '" y1="' + padding.top + '" x2="' + zx + '" y2="' + plotBottom +
        '" stroke="#999" stroke-width="1" shape-rendering="crispEdges"/>';
    } else {
      svgMarkup += '<line x1="' + padding.left + '" y1="' + zeroY + '" x2="' + (padding.left + plotWidth) + '" y2="' + zeroY +
        '" stroke="#999" stroke-width="1" shape-rendering="crispEdges"/>';
    }
  }

  // Bars
  for (si = 0; si < seriesCount; si++) {
    var color = colors[si % colors.length];
    for (ci = 0; ci < catCount; ci++) {
      var idx = si * catCount + ci;
      svgMarkup += '<rect x="' + barX[idx] + '" y="' + barY[idx] + '" width="' + barW[idx] + '" height="' + barH[idx] +
        '" fill="' + color + '" class="chart-bar"/>';
    }
  }

  svgMarkup += '</svg>';

  // ── Phase 3: Build HTML label overlay ──
  var labelMarkup = '<div class="chart-labels">';

  if (showTicks) {
    if (horizontal) {
      // Value tick labels on X-axis (bottom)
      for (ti = 0; ti < tickCount; ti++)
        labelMarkup += '<span class="chart-tick-x" style="left:' + gridPositions[ti] + 'px;top:' + (plotBottom + 6) + 'px">' + gridLabels[ti] + '</span>';

      // Category labels on Y-axis (left, centered on each group)
      for (ci = 0; ci < catCount; ci++) {
        var centerY = (padding.top + ci * groupSize + groupSize / 2) | 0;
        labelMarkup += '<span class="chart-tick-y" style="left:' + (padding.left - 4) + 'px;top:' + (centerY - 1) + 'px">' + labels[ci] + '</span>';
      }
    } else {
      // Value tick labels on Y-axis
      for (ti = 0; ti < tickCount; ti++)
        labelMarkup += '<span class="chart-tick-y" style="left:' + (padding.left - 4) + 'px;top:' + (gridPositions[ti] - 1) + 'px">' + gridLabels[ti] + '</span>';

      // Category labels on X-axis (bottom)
      for (ci = 0; ci < catCount; ci++) {
        var centerX = (padding.left + ci * groupSize + groupSize / 2) | 0;
        labelMarkup += '<span class="chart-tick-x" style="left:' + centerX + 'px;top:' + (plotBottom + 6) + 'px">' + labels[ci] + '</span>';
      }
    }
  }

  // Axis titles
  if (xLabel)
    labelMarkup += '<span class="chart-title" style="left:' + (padding.left + (plotWidth >> 1)) + 'px;top:' + (chartHeight - 10) + 'px">' + xLabel + '</span>';
  if (yLabel)
    labelMarkup += '<span class="chart-title" style="left:4px;top:' + (padding.top - 14) + 'px;transform:none;text-align:left">' + yLabel + '</span>';

  labelMarkup += '</div>';

  // ── Phase 4: Single innerHTML write ──
  container.style.cssText = 'position:relative;width:' + chartWidth + 'px;height:' + chartHeight + 'px';
  container.innerHTML = svgMarkup + labelMarkup;

  // ── Phase 5: Auto-resize ──
  if (!config.width) {
    observeResize(container, function () { NanoBarChart(containerId, config); });
  }

  // ── Phase 6: Lazy hover ──
  if (!hoverEnabled) return;

  var svgEl = container.firstChild as SVGSVGElement;
  var highlight: SVGRectElement | null = null;
  var tooltip: HTMLDivElement | null = null;

  function ensureHover() {
    if (!highlight) {
      highlight = document.createElementNS(SVG_NS, 'rect') as SVGRectElement;
      highlight.setAttribute('class', 'chart-indicator');
      highlight.setAttribute('fill', 'rgba(255,255,255,0.3)');
      highlight.setAttribute('stroke', '#333');
      highlight.setAttribute('stroke-width', '1');
      highlight.style.display = 'none';
      svgEl.appendChild(highlight);
    }
    if (!tooltip) {
      tooltip = document.createElement('div') as HTMLDivElement;
      tooltip.className = 'chart-tooltip';
      container.appendChild(tooltip);
    }
  }

  attachHoverListeners(container, svgEl,
    function (mx, my) {
      if (mx < padding.left || mx > padding.left + plotWidth || my < padding.top || my > plotBottom) {
        if (highlight) highlight.style.display = 'none';
        if (tooltip) tooltip.style.opacity = '0';
        return;
      }

      // Snap to category, then find nearest bar by distance to center
      var nearCat: number;
      if (horizontal) {
        nearCat = ((my - padding.top) / groupSize) | 0;
      } else {
        nearCat = ((mx - padding.left) / groupSize) | 0;
      }
      if (nearCat >= catCount) nearCat = catCount - 1;
      if (nearCat < 0) nearCat = 0;

      var bestIdx = -1;
      var bestDist = Infinity;
      for (si = 0; si < seriesCount; si++) {
        var idx = si * catCount + nearCat;
        var cx = barX[idx] + (barW[idx] >> 1);
        var cy = barY[idx] + (barH[idx] >> 1);
        var dx = mx - cx, dy = my - cy;
        var dist = dx * dx + dy * dy;
        if (dist < bestDist) { bestDist = dist; bestIdx = idx; }
      }

      if (bestIdx === -1) return;

      ensureHover();

      var bsi = (bestIdx / catCount) | 0;
      var bci = bestIdx % catCount;

      highlight!.setAttribute('x', '' + barX[bestIdx]);
      highlight!.setAttribute('y', '' + barY[bestIdx]);
      highlight!.setAttribute('width', '' + barW[bestIdx]);
      highlight!.setAttribute('height', '' + barH[bestIdx]);
      highlight!.setAttribute('stroke', colors[bsi % colors.length]);
      highlight!.style.display = '';

      var tipX: number, tipY: number;
      if (horizontal) {
        tipX = barX[bestIdx] + barW[bestIdx];
        tipY = barY[bestIdx] + (barH[bestIdx] >> 1);
        tooltip!.style.transform = 'translate(' + (tipX + 8) + 'px,' + tipY + 'px) translate(0,-50%)';
      } else {
        tipX = barX[bestIdx] + (barW[bestIdx] >> 1);
        tipY = barY[bestIdx];
        tooltip!.style.transform = 'translate(' + tipX + 'px,' + (tipY - 10) + 'px) translate(-50%,-100%)';
      }

      tooltip!.innerHTML = (seriesCount > 1 ? '<b>Series ' + (bsi + 1) + '</b><br>' : '') +
        labels[bci] + ': ' + data[bsi][bci];
      tooltip!.style.opacity = '1';
    },
    function () {
      if (highlight) highlight.style.display = 'none';
      if (tooltip) tooltip.style.opacity = '0';
    }
  );
}
